import React from 'react';
import { useApp } from '@/context/AppContext';
import { PageHeader, Card, KpiCard } from '@/components/common';
import { formatCurrency } from '@/lib/format';
import { months } from '@/data/mockData';
import { ComposedChart, Bar, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';

const FinancialOverview = () => {
  const { t, tx, lang, data } = useApp();
  const f = data.finance;

  return (
    <div>
      <PageHeader title={t('finance.title')} subtitle={t('finance.subtitle')} />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-5">
        {f.kpis.map((k, i) => <KpiCard key={k.key} item={k} index={i} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-6" data-testid="finance-evolution">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-zinc-400 mb-4">{t('finance.evolution')}</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={f.evolution} margin={{ left: -10, right: 10, top: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f3" vertical={false} />
                <XAxis dataKey="i" tickFormatter={(i) => months[lang][i]} tick={{ fontSize: 11, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} tick={{ fontSize: 11, fill: '#a1a1aa' }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => formatCurrency(v, lang)} contentStyle={{ borderRadius: 12, border: '1px solid #e4e4e7', fontSize: 12 }} labelFormatter={(i) => months[lang][i]} />
                <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => t(`finance.${v}`)} />
                <Bar dataKey="expenses" fill="#E4E4E7" radius={[4, 4, 0, 0]} maxBarSize={22} />
                <Line type="monotone" dataKey="revenue" stroke="#06B6D4" strokeWidth={3} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6" data-testid="finance-breakdown">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-zinc-400 mb-4">{t('finance.breakdown')}</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={f.breakdown} dataKey="value" nameKey="key" innerRadius={55} outerRadius={80} paddingAngle={3}>
                  {f.breakdown.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v, lang)} contentStyle={{ borderRadius: 12, border: '1px solid #e4e4e7', fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-2">
            {f.breakdown.map((b, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-zinc-600"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: b.color }} />{tx(b.key)}</span>
                <span className="font-mono-num font-medium text-zinc-800">{formatCurrency(b.value, lang)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default FinancialOverview;
